import React from 'react';

import { useAppDispatch } from '@/store';
import { fetchStatsDataSet } from '@/store/slices/ownerHubStats';
// Reuse the video-ads campaign<->Stats matching helpers (generic; their only affiliate import is
// type-only, so there is no runtime cycle). Same confirmed source the video library uses: the Stats
// `daily_affiliate_activity` data set aggregated on the `campaign` dimension (= campaign id).
import { campaignGroups, matchedCampaignMetrics, statMatchKeys, sumMetricField } from '@/nova/pages/creatives/video-stats';

import { isoDay } from '../reports-helpers';
import type { AffiliateCampaign } from '../types';
import type { CampaignStat } from '../mapping';

/** How far back to sum campaign activity (matches the video-ads library window). */
const WINDOW_DAYS = 90;

export interface CampaignStatsResource {
  /** Per-campaign activity by campaign id. On a settled successful read EVERY listed campaign has an
   *  entry (its real figures, or zeros when it had no activity in the window). An id is absent only
   *  when the read has not settled or failed for it, which the card shows as loading / "?" glyphs. */
  byId: Record<string, CampaignStat>;
  /** True once the read has settled for the current brand (so an absent id is a real miss, not "loading"). */
  loaded: boolean;
}

/**
 * Per-campaign Regs/FTDs over the last {@link WINDOW_DAYS}, from the Stats `daily_affiliate_activity`
 * data set aggregated on `campaign`, matched to each affiliate campaign by id (campaign_no as a hedge),
 * the same mechanism the video-ads library uses for its per-ad badges. Best-effort enrichment: a failed
 * read keeps the last-known map rather than blanking the cards.
 */
export function useCampaignStats(
  casinoInstanceId: string | undefined,
  campaigns: readonly AffiliateCampaign[],
): CampaignStatsResource {
  const dispatch = useAppDispatch();
  const [byId, setById] = React.useState<Record<string, CampaignStat>>({});
  const [loadedFor, setLoadedFor] = React.useState<string | undefined>(undefined);
  // Stable signature of the campaign id set, so the effect re-runs when the set changes but not on every
  // new array identity (the parent passes a fresh array each render).
  const idsKey = campaigns
    .map((c) => c.id)
    .sort()
    .join(',');

  React.useEffect(() => {
    if (!casinoInstanceId || campaigns.length === 0) {
      setById({});
      // An empty id-set is not "settled": clear loadedFor so `loaded` is false. Otherwise, if the
      // campaigns list momentarily drops to [] (e.g. a list refetch errors and useAffiliateResource
      // clears data), a stale loadedFor would keep `loaded` true and flash the "?" placeholder glyphs
      // instead of skeletons when the list comes back, until the stats effect re-runs and re-settles.
      setLoadedFor(undefined);
      return undefined;
    }
    let stale = false;
    const today = new Date();
    const to = new Date(today);
    to.setDate(to.getDate() + 1); // end-exclusive: include today
    const from = new Date(today);
    from.setDate(from.getDate() - WINDOW_DAYS);

    (async () => {
      const entry = await dispatch(
        fetchStatsDataSet({
          casinoInstanceId,
          dataSet: 'daily_affiliate_activity',
          startDate: isoDay(from),
          endDate: isoDay(to),
          aggregateOn: ['campaign'],
        }),
      ).unwrap();
      const groups = campaignGroups(entry.data);
      const out: Record<string, CampaignStat> = {};
      for (const c of campaigns) {
        const keys = statMatchKeys([c]);
        if (keys.size === 0) continue;
        const metricsList = matchedCampaignMetrics(groups, keys);
        out[c.id] = {
          registrations: sumMetricField(metricsList, 'registrations'),
          ftds: sumMetricField(metricsList, 'ftds'),
        };
      }
      if (!stale) {
        setById(out);
        setLoadedFor(casinoInstanceId);
      }
    })().catch(() => {
      // A failed read still SETTLES: keep the last-known map (don't blank it), but mark loaded so the cards
      // fall back to the "?" placeholder glyphs rather than shimmering forever (the roster convention: a
      // failed figure shows a glyph, never a perpetual skeleton). There is no in-app retry, so not settling
      // here would leave the tiles loading for the whole session.
      if (!stale) setLoadedFor(casinoInstanceId);
    });
    return () => {
      stale = true;
    };
    // idsKey captures the campaign set; brand + dispatch complete the deps. The campaigns array identity
    // changes every render, so we key on the derived idsKey instead of the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casinoInstanceId, idsKey, dispatch]);

  return { byId, loaded: casinoInstanceId !== undefined && loadedFor === casinoInstanceId };
}
